import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { listResource, getResource, createResource, updateResource } from '@diggens/api-client';
import type { User } from '@diggens/types';

export function useUsers(tenantId?: string, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ['users', tenantId],
    enabled: options.enabled ?? true,
    queryFn: async () => {
      const params: Record<string, string | undefined> = { tenant_id: tenantId };
      const { data } = await listResource<User>('users', params);
      return data;
    },
  });
}

export function useUsersByPortfolioCompany(portfolioCompanyId: string) {
  return useQuery({
    queryKey: ['users', 'portfolioCompany', portfolioCompanyId],
    queryFn: async () => {
      const { data } = await listResource<User>('users', { portfolio_company_id: portfolioCompanyId });
      return data;
    },
    enabled: !!portfolioCompanyId,
  });
}

export function useUser(userId: string) {
  return useQuery({
    queryKey: ['users', userId],
    queryFn: () => getResource<User>('users', userId),
    enabled: !!userId,
  });
}

export function useCreateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { id: string; tenant_id: string | null; portfolio_company_id: string | null; role: 'super_admin' | 'admin' | 'portco'; email: string; full_name: string | null }) =>
      createResource<User>('users', input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables: { userId: string; updates: Partial<User> }) =>
      updateResource<User>('users', variables.userId, variables.updates),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['users', variables.userId] });
    },
  });
}
